import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DisplayContainer from "../../../../components/displayContainer/DisplayContainer";
import RefreshIcon from "../../../../components/icons/RefreshIcon";
import Loader from "../../../../components/loader/Loader";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import TimelineTable from "../../../../components/timelineTable/TimelineTable";
import { fetchControlledSetNumbers } from "../../../../features/controlledSetNumbers/controlledSetNumbersSlice";
import { fetchTimelineSetByTournamentId } from "../../../../features/timelines/timelinesSlice";
import { fetchTournamentsByStatuses } from "../../../../features/tournaments/tournamentsSlice";
import { getLocalizedTime } from "../../../../utils/timeZone";
import styles from "./allTimelines.module.css";

const STATUS_LABELS = {
  live: "مباشرة",
  upcoming: "قادمة",
};

const AllTimelines = () => {
  const [selectedTournament, setSelectedTournament] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const dispatch = useDispatch();
  const {
    filteredList: tournaments,
    fetchFilteredStatus,
    currentFilteredPage,
    totalFilteredPages,
  } = useSelector((state) => state.tournaments);
  const {
    results: timeliens,
    cache,
    status: fetchTimelinesStatus,
  } = useSelector((state) => state.timelines);
  const { fetchStatus: fetchControlledSetNumbersStatus, controlledSetNumbers } =
    useSelector((state) => state.controlledSetNumbers);

  const { timeZone } = useSelector((state) => state.timeZone);
  const timelineRequestRef = useRef("");
  const controlledSetNumbersRequestRef = useRef("");
  const cachedTimeline = selectedTournament ? cache[selectedTournament] : null;
  const hasControlledSetNumbers = Boolean(
    selectedTournament && controlledSetNumbers[selectedTournament],
  );

  useEffect(() => {
    if (!selectedTournament) return;

    if (cachedTimeline) {
      dispatch({
        type: "timelines/updateResultsFromCache",
        payload: cachedTimeline,
      });
      timelineRequestRef.current = "";
    } else if (timelineRequestRef.current !== selectedTournament) {
      timelineRequestRef.current = selectedTournament;
      dispatch(fetchTimelineSetByTournamentId(selectedTournament));
    }

    if (hasControlledSetNumbers) {
      controlledSetNumbersRequestRef.current = "";
    } else if (
      controlledSetNumbersRequestRef.current !== selectedTournament
    ) {
      controlledSetNumbersRequestRef.current = selectedTournament;
      dispatch(fetchControlledSetNumbers(selectedTournament));
    }
  }, [cachedTimeline, dispatch, hasControlledSetNumbers, selectedTournament]);

  const tournamentListRef = useRef(null);

  const handleRefreshTimelines = useCallback(async () => {
    setIsRefreshing(true);
    tournamentListRef.current?.scrollTo?.({
      top: 0,
      behavior: "smooth",
    });

    try {
      const refreshRequests = [
        dispatch(
          fetchTournamentsByStatuses({
            page: 1,
            statuses: ["upcoming", "live"],
          }),
        ),
      ];

      if (selectedTournament) {
        refreshRequests.push(dispatch(fetchTimelineSetByTournamentId(selectedTournament)));
        refreshRequests.push(dispatch(fetchControlledSetNumbers(selectedTournament)));
      }

      await Promise.all(refreshRequests);
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch, selectedTournament]);

  const handleScroll = useCallback(() => {
    const container = tournamentListRef.current;
    if (!container || fetchFilteredStatus === "loading") return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      currentFilteredPage < totalFilteredPages
    ) {
      dispatch(
        fetchTournamentsByStatuses({
          page: currentFilteredPage + 1,
          statuses: ["upcoming", "live"],
        }),
      );
    }
  }, [dispatch, fetchFilteredStatus, currentFilteredPage, totalFilteredPages]);

  useEffect(() => {
    if (fetchFilteredStatus === "idle") {
      dispatch(
        fetchTournamentsByStatuses({ page: 1, statuses: ["upcoming", "live"] }),
      );
    }
  }, [dispatch, fetchFilteredStatus]);

  useEffect(() => {
    const container = tournamentListRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [dispatch, currentFilteredPage, totalFilteredPages, handleScroll]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitleGroup}>
          <TextWrapper textStyle="textTitle">جميع الجداول الزمنية</TextWrapper>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => {
              Promise.resolve(handleRefreshTimelines()).catch(() => {});
            }}
            disabled={
              fetchFilteredStatus === "loading" ||
              fetchTimelinesStatus === "loading" ||
              fetchControlledSetNumbersStatus === "loading" ||
              isRefreshing
            }
            title="تحديث الجداول الزمنية"
            aria-label="تحديث الجداول الزمنية"
          >
            {isRefreshing ? (
              <Loader size={18} color="var(--color-accent-primary)" />
            ) : (
              <RefreshIcon className={styles.refreshIcon} />
            )}
          </button>
        </div>
      </div>
      <div className={styles.displayContainerWrapper}>
        <DisplayContainer className={styles.timelinesDisplayContainer}>
          <div className={styles.allTimeLinesWrapper}>
            <div className={styles.tournamentsWrapper} ref={tournamentListRef}>
              <div className={styles.sectionHeader}>
                <TextWrapper textStyle={"textMid"}>
                  البطولات المباشرة والقادمة
                </TextWrapper>
              </div>

              {tournaments.map((item) => {
                const localizedStartDate = getLocalizedTime(
                  item.startDate,
                  timeZone,
                );

                return (
                  <button
                    type="button"
                    key={item._id}
                    className={
                      selectedTournament === item._id
                        ? `${styles.tournamentWrapper} ${styles.active}`
                        : styles.tournamentWrapper
                    }
                    onClick={() => setSelectedTournament(item._id)}
                  >
                    <div className={styles.tournamentMeta}>
                      <span className={styles.metaLabel}>الاسم</span>
                      <p className={styles.metaValue}>{item.name}</p>
                    </div>

                    <div className={styles.metaGrid}>
                      <div className={styles.metaCard}>
                        <span className={styles.metaLabel}>الرقم التسلسلي</span>
                        <p className={styles.metaValue}>
                          {item.serialNumber || "-"}
                        </p>
                      </div>
                      <div className={styles.metaCard}>
                        <span className={styles.metaLabel}>الحالة</span>
                        <p className={styles.metaValue}>
                          {STATUS_LABELS[item.status] || item.status}
                        </p>
                      </div>
                    </div>

                    <div className={styles.tournamentMeta}>
                      <span className={styles.metaLabel}>تبدأ من تاريخ</span>
                      <p className={styles.metaValue}>
                        {localizedStartDate.toLocaleString().split(",")[0]}
                      </p>
                    </div>
                  </button>
                );
              })}

              {fetchFilteredStatus !== "loading" && tournaments.length === 0 && (
                <div className={styles.emptyState}>
                  <TextWrapper textStyle={"textMid"}>
                    لا توجد بطولات مباشرة أو قادمة
                  </TextWrapper>
                </div>
              )}

              {fetchFilteredStatus === "loading" && (
                <div className={styles.loaderContainer}>
                  <Loader />
                </div>
              )}
            </div>

            <div className={styles.timelineContainer}>
              {selectedTournament === "" ? (
                <div className={styles.placeholderState}>
                  <TextWrapper textStyle={"textTitle"}>اختر بطولة</TextWrapper>
                  <TextWrapper textStyle={"textMid"}>
                    اختر بطولة من القائمة لعرض الجدول الزمني الخاص بها
                  </TextWrapper>
                </div>
              ) : fetchTimelinesStatus === "loading" ||
                fetchControlledSetNumbersStatus === "loading" ? (
                <div className={styles.loaderContainer}>
                  <Loader />
                </div>
              ) : timeliens.length === 0 ? (
                <div className={styles.placeholderState}>
                  <TextWrapper textStyle={"textTitle"}>
                    لا يوجد جدول زمني
                  </TextWrapper>
                  <TextWrapper textStyle={"textMid"}>
                    لم يتم إنشاء مواعيد لهذه البطولة بعد
                  </TextWrapper>
                </div>
              ) : (
                <TimelineTable
                  timelineData={timeliens}
                  tournamentId={selectedTournament}
                />
              )}
            </div>
          </div>
        </DisplayContainer>
      </div>
    </div>
  );
};

export default AllTimelines;
